import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {Button, DatePicker, DescriptiveText, Dropdown, Icon, NumberInput, Row} from '../index';
import moment from 'moment';
import {v4 as uuid} from 'uuid';
import {getStepTwoCriteria, initialStepTwoCriteriaOptions, updateStepTwoCriteriaOptions} from '../utils/utils';

const stepOneCriteria = [
  {label: 'Fund in a Category', value: 'fundCategory'},
  {label: 'Stock in a Sector', value: 'stockSector'},
  {label: 'Stock in a Region', value: 'region'},
  {label: 'Stock in a Country', value: 'country'},
  {label: 'Bond in a Sector', value: 'bondSector'},
  {label: 'Individual Security', value: 'securityID'},
  {label: 'Exposure', value: 'assetClass'},
  {label: 'Portfolio Value', value: 'balance'},
  {label: 'Age', value: 'age'},
  {label: 'Account Type', value: 'accountType'},
  {label: 'Target Risk', value: 'targetRisk'},
  {label: 'Actual Risk', value: 'actualRisk'},
  {label: 'Probability of Success', value: 'successProbability'},
  {label: 'Latest Insight Date', value: 'insightDate'},
];

const Criterion = (_props) => {
  const {
    criterion, remainingOptions, selectedCriteria, stepTwoCriteria, updateCriteria, removeCriteria,
    updateCriteriaValueDropdown, updateCriteriaValueNumber, updateCriteriaValueDate, readOnly,
  } = _props;

  const renderStepTwoCriteria = () => {
    if (stepTwoCriteria[selectedCriteria[criterion].id]) {
      switch (selectedCriteria[criterion].id) {
        case 'assetClass':
          return <>
            <StepTwoDropdown key={selectedCriteria[criterion].id + 'StepTwoDropdown'}
                             criterion={criterion}
                             selectedCriteria={selectedCriteria}
                             stepTwoCriteria={stepTwoCriteria}
                             updateCriteriaValueDropdown={updateCriteriaValueDropdown}
                             width={'100%'}
                             readOnly={readOnly}
            />
            {!!selectedCriteria[criterion].value
              ? <StepTwoNumber criterion={criterion}
                               selectedCriteria={selectedCriteria}
                               stepTwoCriteria={stepTwoCriteria}
                               updateCriteriaValueNumber={updateCriteriaValueNumber}
                               readOnly={readOnly}
              /> : null}
          </>;
        case 'accountType':
        case 'bondSector':
        case 'country':
        case 'fundCategory':
        case 'region':
        case 'securityID':
        case 'stockSector':
          return <StepTwoDropdown key={selectedCriteria[criterion].id + 'StepTwoDropdown'}
                                  criterion={criterion}
                                  selectedCriteria={selectedCriteria}
                                  stepTwoCriteria={stepTwoCriteria}
                                  updateCriteriaValueDropdown={updateCriteriaValueDropdown}
                                  width={'100%'}
                                  readOnly={readOnly}
          />;
        case 'actualRisk':
        case 'age':
        case 'balance':
        case 'successProbability':
        case 'targetRisk':
          return <StepTwoNumber criterion={criterion}
                                selectedCriteria={selectedCriteria}
                                stepTwoCriteria={stepTwoCriteria}
                                updateCriteriaValueNumber={updateCriteriaValueNumber}
                                readOnly={readOnly}
          />;
        case 'insightDate':
          return <StepTwoDate key={selectedCriteria[criterion].id}
                              criterion={criterion}
                              selectedCriteria={selectedCriteria}
                              updateCriteriaValueDate={updateCriteriaValueDate}
                              readOnly={readOnly}
          />;
        default:
          return null;
      }
    }
  };

  return <Row wrap={false}>
    <Dropdown title={'Criterion Type'}
              dataPoint={{
                name: criterion,
                value: {
                  label: selectedCriteria[criterion].label,
                  value: selectedCriteria[criterion].value,
                },
              }}
              placeholder={selectedCriteria[criterion].label}
              type='typeahead'
              onChange={(name, option) => {
                updateCriteria(criterion, option);
              }}
              options={remainingOptions}
              width={'100%'}
              readOnly={readOnly}
    />
    {renderStepTwoCriteria()}
    {!readOnly && Object.keys(selectedCriteria).length > 1
      ? <Icon svg={'circleCancel'}
              height={'20px'}
              margin={'12px 0px 0px'}
              action={() => removeCriteria(criterion)}/>
      : null}
  </Row>;
};

const StepTwoDropdown = (_props) => {
  const {criterion, selectedCriteria, stepTwoCriteria, updateCriteriaValueDropdown, readOnly} = _props;

  return <Dropdown title={selectedCriteria[criterion].label}
                   dataPoint={{
                     name: selectedCriteria[criterion].id,
                     value: {
                       label: selectedCriteria[criterion].id === 'securityID'
                         ? selectedCriteria[criterion].securityName
                         : selectedCriteria[criterion].value,
                       value: selectedCriteria[criterion].value,
                     },
                   }}
                   placeholder={selectedCriteria[criterion].value}
                   type='typeahead'
                   onChange={(name, option) => {
                     updateCriteriaValueDropdown(criterion, option);
                   }}
                   options={stepTwoCriteria[selectedCriteria[criterion].id]
                   && stepTwoCriteria[selectedCriteria[criterion].id].options}
                   width={'100%'}
                   readOnly={readOnly}
  />;
};

const StepTwoNumber = (_props) => {
  const {criterion, selectedCriteria, stepTwoCriteria, updateCriteriaValueNumber, readOnly} = _props;
  const name = selectedCriteria[criterion].id;
  return <>
    <NumberInput key={name + 'StepTwoGreaterThan'}
                 type={stepTwoCriteria[name].format || 'number'}
                 title={name === 'age' ? 'Older Than' : 'Greater Than'}
                 placeholder={stepTwoCriteria[name].placeholder || '0'}
                 decimalPlaces={stepTwoCriteria[name].decimalPlaces || 0}
                 dataPoint={{
                   name: criterion + '_greaterThan',
                   value: selectedCriteria[criterion].greaterThan,
                 }}
                 onBlur={updateCriteriaValueNumber}
                 readOnly={readOnly}/>
    <NumberInput key={name + 'StepTwoLessThan'}
                 type={stepTwoCriteria[name].format || 'number'}
                 title={name === 'age' ? 'Younger Than' : 'Less Than'}
                 placeholder={stepTwoCriteria[name].placeholder || '0'}
                 decimalPlaces={stepTwoCriteria[name].decimalPlaces || 0}
                 dataPoint={{
                   name: criterion + '_lessThan',
                   value: selectedCriteria[criterion].lessThan,
                 }}
                 onBlur={updateCriteriaValueNumber}
                 readOnly={readOnly}/>
  </>;
};

const StepTwoDate = (_props) => {
  const {criterion, selectedCriteria, updateCriteriaValueDate, readOnly} = _props;
  return <>
    <div style={{
      maxWidth: '100%',
      margin: '5px',
      width: '100%',
    }}>
      <DatePicker key={criterion}
                  title={'No Insights Received Since'}
                  layout={{grow: 1}}
                  dataPoint={{
                    name: selectedCriteria[criterion].id,
                    value: selectedCriteria[criterion].date,
                  }}
                  onChange={(name, value) => {
                    updateCriteriaValueDate(criterion, value);
                  }}
                  maxDate={moment().format('YYYY-MM-DD')}
                  readOnly={readOnly}/>
    </div>
  </>;
};

const CriteriaSelector = (props) => {
  const {selectedCriteria, setSelectedCriteria, setButtonDisabled, readOnly, prepopulatedOptions} = props;
  const [remainingOptions, setRemainingOptions] = useState(stepOneCriteria);
  const [stepTwoCriteria, setStepTwoCriteria] = useState(prepopulatedOptions || initialStepTwoCriteriaOptions);

  const addCriteria = () => {
    let newSelected = {
      ...selectedCriteria,
      [uuid()]: {id: ''},
    };
    setSelectedCriteria(newSelected);
  };
  const updateRemainingSelected = () => {
    let newRemainingOptions = stepOneCriteria.filter(option => !Object.values(selectedCriteria)
      .map(sc => sc.id)
      .includes(option.value));
    setRemainingOptions(newRemainingOptions);
  };

  const updateButtonDisabled = () => {
    let disabled = true;
    for (let i = 0; i < Object.values(selectedCriteria).length; i++) {
      if (Object.values(selectedCriteria)[i].value
        || Object.values(selectedCriteria)[i].greaterThan
        || Object.values(selectedCriteria)[i].lessThan
        || Object.values(selectedCriteria)[i].date) {
        disabled = false;
        break;
      }
    }
    if (setButtonDisabled) {
      setButtonDisabled(disabled);
    }
  };

  const updateCriteria = (name, option) => {
    let newSelected = selectedCriteria;
    newSelected[name] = {
      id: option.value,
      label: option.label,
    };
    setSelectedCriteria(newSelected);
    updateRemainingSelected();
    updateButtonDisabled();
    getStepTwoCriteria([option.value])
      .then(function success(result) {
        if (result) {
          const newStepTwoCriteriaOptions = {...updateStepTwoCriteriaOptions(result, [selectedCriteria[name].id])};
          setStepTwoCriteria(newStepTwoCriteriaOptions);
        }
      });
  };

  const removeCriteria = (name) => {
    let newSelected = selectedCriteria;
    delete newSelected[name];
    setSelectedCriteria({...newSelected});
    updateRemainingSelected();
    updateButtonDisabled();
  };
  const updateCriteriaValueDropdown = (name, option) => {
    let newSelected = selectedCriteria;
    newSelected[name].value = option.value;
    delete newSelected[name].securityName;
    if (selectedCriteria[name].id === 'securityID') {
      newSelected[name].securityName = option.label;
    }
    setSelectedCriteria({...newSelected});
    updateButtonDisabled();
  };
  const updateCriteriaValueNumber = (_name, val) => {
    let newSelected = selectedCriteria;
    const name = _name.substring(0, _name.indexOf('_'));
    const greaterOrLessThan = _name.substring(_name.indexOf('_') + 1, _name.length);
    newSelected[name][greaterOrLessThan] = val;
    setSelectedCriteria({...newSelected});
    updateButtonDisabled();
  };
  const updateCriteriaValueDate = (name, val) => {
    let newSelected = selectedCriteria;
    newSelected[name].date = val;
    setSelectedCriteria({...newSelected});
    updateButtonDisabled();
  };

  useEffect(() => {
    updateRemainingSelected();
    let selectedCriteriaArr = [];
    Object.keys(selectedCriteria).map(name => {
      selectedCriteriaArr.push(selectedCriteria[name].id);
    });
    getStepTwoCriteria(selectedCriteriaArr)
      .then(function success(result) {
        if (result) {
          const newStepTwoCriteriaOptions = {...updateStepTwoCriteriaOptions(result, selectedCriteriaArr)};
          setStepTwoCriteria(newStepTwoCriteriaOptions);
        }
      });
  }, []);

  return <div>
    {Object.keys(selectedCriteria)
      .map((criterion) => <Criterion key={criterion}
                                     criterion={criterion}
                                     remainingOptions={remainingOptions}
                                     selectedCriteria={selectedCriteria}
                                     stepTwoCriteria={stepTwoCriteria}
                                     updateCriteria={updateCriteria}
                                     removeCriteria={removeCriteria}
                                     updateCriteriaValueDropdown={updateCriteriaValueDropdown}
                                     updateCriteriaValueNumber={updateCriteriaValueNumber}
                                     updateCriteriaValueDate={updateCriteriaValueDate}
                                     readOnly={readOnly}/>)}
    {readOnly ? null
      : <Row wrap={false}>
        <Button importance={'secondary'}
                width={'fit-content'}
                size={'md'}
                label={'Additional Criteria'}
                disabled={false}
                action={() => addCriteria()}/>
        <DescriptiveText width={'200px'} layout={{grow: 1}}>
          Note: Households will only pass if they meet ALL criteria.
        </DescriptiveText>
      </Row>}
  </div>;
};

export default CriteriaSelector;

CriteriaSelector.propTypes = {
  /**
   * The parent component's selectedCriteria state.
   */
  selectedCriteria: PropTypes.shape({}).isRequired,
  /**
   * The function that sets the parent component's selectedCriteria state.
   */
  setSelectedCriteria: PropTypes.func.isRequired,
  /**
   * The function that sets the parent component's disabled state to true or false.
   */
  setButtonDisabled: PropTypes.func,
  readOnly: PropTypes.bool,
  prepopulatedOptions: PropTypes.object,
};
